import api from "../api/api";
import { logger } from "../utils/logger";
export class NotificationService {
  static isPushNotificationSupported() {
    // Check if the browser supports service workers
    const hasServiceWorkerSupport = "serviceWorker" in navigator;

    // Check if the browser supports Push API
    const hasPushManagerSupport = "PushManager" in window;

    // Check if it's iOS
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Check if it's running as standalone PWA on iOS
    const isIOSPWA = isIOS && window.navigator.standalone;

    // Check if notifications are supported
    const hasNotificationSupport = "Notification" in window;

    return {
      isSupported:
        hasServiceWorkerSupport &&
        hasPushManagerSupport &&
        hasNotificationSupport &&
        (!isIOS || isIOSPWA),
      details: {
        serviceWorker: hasServiceWorkerSupport,
        pushManager: hasPushManagerSupport,
        notifications: hasNotificationSupport,
        isIOS: isIOS,
        isIOSPWA: isIOSPWA,
      },
    };
  }
  static async checkSubscription() {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        return false;
      }
      const isRegistered = await navigator.serviceWorker.getRegistration();
      if (!isRegistered) {
        return false;
      }
      const registration = await navigator.serviceWorker.ready;

      const subscription = await registration.pushManager.getSubscription();

      if (!subscription?.endpoint) {
        return false;
      }

      const response = await api.get("/push-subscriptions/check/", {
        params: {
          endpoint: subscription.endpoint,
        },
      });

      return response.data;
    } catch (error) {
      logger.error("Error checking subscription:", {
        url: "/push-subscriptions/check/",
        error,
      });
      return false;
    }
  }

  // Add this at the class level
  static _subscriptionInProgress = false;

  static async subscribe() {
    // Prevent multiple subscription attempts from running in parallel
    if (this._subscriptionInProgress) {
      throw new Error("A subscription request is already in progress");
    }

    this._subscriptionInProgress = true;

    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Push notifications not supported");
      }

      // Check if VAPID key is available
      if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
        throw new Error("VAPID public key is missing");
      }

      // Check permission state - do this check as late as possible
      if (Notification.permission === "denied") {
        throw new Error("Notification permission has been denied");
      }

      // If permission isn't granted yet, request it before subscribing
      if (Notification.permission !== "granted") {
        const permission = await Notification.requestPermission();

        // Check again after the user has interacted with the prompt
        if (permission !== "granted") {
          throw new Error("Notification permission not granted");
        }
      }

      // Register service worker (consider moving this to app initialization)
      const registration = await navigator.serviceWorker.register(
        `/service-worker.js`
      );
      await navigator.serviceWorker.ready;

      // Check for existing subscription
      const existingSubscription =
        await registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      // Create new subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          process.env.REACT_APP_VAPID_PUBLIC_KEY
        ),
      });

      // Save subscription to server
      await api.post("/push-subscriptions/", {
        endpoint: subscription.endpoint,
        p256dh: btoa(
          String.fromCharCode.apply(
            null,
            new Uint8Array(subscription.getKey("p256dh"))
          )
        ),
        auth: btoa(
          String.fromCharCode.apply(
            null,
            new Uint8Array(subscription.getKey("auth"))
          )
        ),
      });

      return subscription;
    } catch (error) {
      logger.error("Push subscription failed", {
        permission: Notification.permission,
        error: error.message,
      });
      throw error;
    } finally {
      // Always release the lock, even if there's an error
      this._subscriptionInProgress = false;
    }
  }
  static async updateSubscription(preferredTime, timezone, userId) {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        throw new Error("No existing subscription found");
      }

      // Build update object with only provided parameters
      const updateData = {};
      if (preferredTime !== null && preferredTime !== undefined) {
        updateData.notification_preferred_time = preferredTime;
      }
      if (timezone !== null && timezone !== undefined) {
        updateData.notification_timezone = timezone;
      }

      await api.patch(`/users/${userId}/`, updateData);

      return subscription;
    } catch (error) {
      console.error("Error updating subscription:", error);
      throw error;
    }
  }

  static async unsubscribe() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await api.delete("/push-subscriptions/", {
          params: { endpoint: subscription.endpoint },
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error unsubscribing:", error);
      throw error;
    }
  }

  static async checkForUpdates() {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();

      if (registration.waiting) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking for updates:", error);
      throw error;
    }
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default NotificationService;
